import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ITranscriptionMessage } from "../../hexagon/core/ports/HealthScribe";

export type VisitType =
  | "START_OF_CARE"
  | "PROVIDER_INITIAL"
  | "HP"
  | "FOLLOW_UP"
  | "DISCHARGE";

interface HealthScribeState {
  isActive: boolean;
  isPaused: boolean;
  isHidden: boolean;
  isLoading: boolean; // Track loading state during session initialization
  visitType: VisitType;
  startTime: string | null; // ISO string for persistence
  sessionId: string | null; // Track the backend session ID
  clinicalSuggestionsEnabled: boolean;
  patientId: string | number | null; // Track which patient the recording is for
  patientName: string | null; // Store patient name for display
  connectionStatus: "connected" | "disconnected" | "error" | null; // WebSocket status
  audioLevel: number; // Current microphone audio level (0-100)
  transcription: ITranscriptionMessage[]; // Use proper type from hexagon
  noteToOverwrite: string | null;
}

const initialState: HealthScribeState = {
  isActive: false,
  isPaused: false,
  isHidden: false,
  isLoading: false,
  visitType: "FOLLOW_UP",
  startTime: null,
  sessionId: null,
  clinicalSuggestionsEnabled: true,
  patientId: null,
  patientName: null,
  connectionStatus: null,
  audioLevel: 0,
  transcription: [],
  noteToOverwrite: null,
};

const healthScribeSlice = createSlice({
  name: "healthScribe",
  initialState,
  reducers: {
    // New action to immediately show UI with loading state
    initializeHealthScribe: (
      state,
      action: PayloadAction<{
        visitType: VisitType;
        patientId?: string | number;
        patientName?: string;
      }>
    ) => {
      state.isActive = true;
      state.isPaused = false;
      state.isHidden = false;
      state.isLoading = true;
      state.visitType = action.payload.visitType;
      state.patientId = action.payload.patientId || null;
      state.patientName = action.payload.patientName || null;
      state.startTime = new Date().toISOString();
      state.sessionId = null;
      state.connectionStatus = null;
      state.transcription = [];
    },

    startHealthScribe: (
      state,
      action: PayloadAction<{
        visitType: VisitType;
        sessionId: string;
        patientId?: string | number;
        patientName?: string;
      }>
    ) => {
      state.isActive = true;
      state.isPaused = false;
      state.isHidden = false;
      state.isLoading = false;
      state.visitType = action.payload.visitType;
      state.sessionId = action.payload.sessionId;
      state.patientId = action.payload.patientId || null;
      state.patientName = action.payload.patientName || null;
      state.startTime = new Date().toISOString();
      state.connectionStatus = null;
      state.transcription = [];
    },

    // Action to handle initialization failure
    healthScribeInitializationFailed: (state) => {
      state.isActive = false;
      state.isPaused = false;
      state.isHidden = false;
      state.isLoading = false;
      state.startTime = null;
      state.sessionId = null;
      state.patientId = null;
      state.patientName = null;
      state.connectionStatus = null;
      state.transcription = [];
    },

    pauseHealthScribe: (state) => {
      state.isPaused = true;
    },

    resumeHealthScribe: (state) => {
      state.isPaused = false;
    },

    stopHealthScribe: (
      state,
      action: PayloadAction<{ noteToOverwrite: string }>
    ) => {
      state.isActive = false;
      state.isPaused = false;
      state.isHidden = false;
      state.isLoading = false;
      state.startTime = null;
      state.sessionId = null;
      state.patientId = null;
      state.patientName = null;
      state.connectionStatus = null;
      state.noteToOverwrite = action.payload.noteToOverwrite;
      // Note: Keep transcription data until explicitly cleared for final processing
    },

    hideHealthScribe: (state) => {
      state.isHidden = true;
    },

    showHealthScribe: (state) => {
      state.isHidden = false;
    },

    setVisitType: (state, action: PayloadAction<VisitType>) => {
      state.visitType = action.payload;
    },

    setPatientInfo: (
      state,
      action: PayloadAction<{
        patientId: string | number;
        patientName?: string;
      }>
    ) => {
      state.patientId = action.payload.patientId;
      if (action.payload.patientName) {
        state.patientName = action.payload.patientName;
      }
    },

    toggleClinicalSuggestions: (state, action: PayloadAction<boolean>) => {
      state.clinicalSuggestionsEnabled = action.payload;
    },

    setConnectionStatus: (
      state,
      action: PayloadAction<"connected" | "disconnected" | "error">
    ) => {
      state.connectionStatus = action.payload;
    },

    setAudioLevel: (state, action: PayloadAction<number>) => {
      state.audioLevel = action.payload;
    },

    addTranscription: (state, action: PayloadAction<ITranscriptionMessage>) => {
      state.transcription.push(action.payload);
    },

    updateTranscription: (
      state,
      action: PayloadAction<ITranscriptionMessage>
    ) => {
      const existingIndex = state.transcription.findIndex(
        (msg) => msg.id === action.payload.id
      );
      if (existingIndex !== -1) {
        // Update existing message
        state.transcription[existingIndex] = action.payload;
      } else {
        // Add new message if not found
        state.transcription.push(action.payload);
      }
    },

    clearTranscription: (state) => {
      state.transcription = [];
    },

    setNoteToOverwrite: (state, action: PayloadAction<string | null>) => {
      state.noteToOverwrite = action.payload;
    },
    setHealthScribeState(state, action: PayloadAction<HealthScribeState>) {
      return action.payload;
    },
  },
});

export const {
  initializeHealthScribe,
  startHealthScribe,
  healthScribeInitializationFailed,
  pauseHealthScribe,
  resumeHealthScribe,
  stopHealthScribe,
  hideHealthScribe,
  showHealthScribe,
  setVisitType,
  setPatientInfo,
  toggleClinicalSuggestions,
  setConnectionStatus,
  setAudioLevel,
  addTranscription,
  updateTranscription,
  clearTranscription,
  setNoteToOverwrite,
  setHealthScribeState,
} = healthScribeSlice.actions;

export default healthScribeSlice.reducer;

// Selectors
export const selectCompleteTranscription = (state: {
  healthScribe: HealthScribeState;
}): string => {
  // Get only final (non-partial) transcription messages and combine them
  const finalMessages = state.healthScribe.transcription
    .filter((msg) => !msg.isPartial)
    .sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

  return finalMessages
    .map((msg) => `${msg.speaker}: ${msg.message}`)
    .join("\n");
};
